"use client"

import { useLocation } from "react-router-dom"
import { useCart } from "../CartContent"
import { useState, useEffect } from "react"
import Navbar from "./Components/Navbar"
import Footer from "./Components/Footer"
import { API_BASE_URL } from "../config"
import { CheckCircle } from "lucide-react"

const Product = () => {
  const location = useLocation()
  const { cartItems, addToCart } = useCart()

  const totalQuantity = cartItems.reduce((sum, item) => sum + item.quantity, 0)

  const { id, image, name, description, variations = [], modifierListInfo = [] } = location.state || {}

  const [quantity, setQuantity] = useState(1)
  const [selectedVariation, setSelectedVariation] = useState(variations[0] || null)
  const [selectedModifiers, setSelectedModifiers] = useState({})
  const [modifierDetails, setModifierDetails] = useState({})
  const [loadingModifiers, setLoadingModifiers] = useState(false)
  const [specialNote, setSpecialNote] = useState("")
  const [showNotification, setShowNotification] = useState(false)
  const [notificationMessage, setNotificationMessage] = useState("")

  // Fetch modifier details when component mounts
  useEffect(() => {
    const fetchModifiers = async () => {
      if (modifierListInfo.length === 0) return

      setLoadingModifiers(true)
      try {
        const modifierIds = modifierListInfo.map((info) => info.modifier_list_id)
        const response = await fetch(`${API_BASE_URL}/api/modifiers?ids=${modifierIds.join(",")}`)

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        console.log("Fetched modifier details:", data)
        setModifierDetails(data.modifierLists || {})
      } catch (error) {
        console.error("Error fetching modifiers:", error)
      } finally {
        setLoadingModifiers(false)
      }
    }

    fetchModifiers()
  }, [modifierListInfo])

  useEffect(() => {
    console.log("Product object from Square:", location.state)
    console.log("Modifier List Info:", modifierListInfo)
  }, [location.state, modifierListInfo])

  // Toggle modifier selection
  const toggleModifier = (modifierListId, modifierId) => {
    setSelectedModifiers((prev) => {
      const currentList = prev[modifierListId] || []

      if (currentList.includes(modifierId)) {
        // Remove modifier
        return {
          ...prev,
          [modifierListId]: currentList.filter((id) => id !== modifierId),
        }
      } else {
        // Add modifier (check if it's single-select)
        const modifierList = modifierDetails[modifierListId]
        const isSingleSelect = modifierList?.modifier_list_data?.selection_type === "SINGLE"

        return {
          ...prev,
          [modifierListId]: isSingleSelect ? [modifierId] : [...currentList, modifierId],
        }
      }
    })
  }

  // Calculate total price: base variation + selected modifiers
  const variationPrice = selectedVariation?.item_variation_data?.price_money?.amount || 0

  // Calculate modifier price
  let modifierPrice = 0
  Object.entries(selectedModifiers).forEach(([modifierListId, modifierIds]) => {
    const modifierList = modifierDetails[modifierListId]
    if (modifierList?.modifier_list_data?.modifiers) {
      modifierIds.forEach((modifierId) => {
        const modifier = modifierList.modifier_list_data.modifiers.find((m) => m.id === modifierId)
        if (modifier?.modifier_data?.price_money?.amount) {
          modifierPrice += modifier.modifier_data.price_money.amount
        }
      })
    }
  })

  const totalPrice = (variationPrice + modifierPrice) / 100 // Convert cents to dollars

  const handleAddToCart = () => {
    const variationId = selectedVariation?.id || "default"
    const variationName = selectedVariation?.item_variation_data?.name || ""

    // Build modifier names for display
    const modifierNames = []
    Object.entries(selectedModifiers).forEach(([modifierListId, modifierIds]) => {
      const modifierList = modifierDetails[modifierListId]
      if (modifierList?.modifier_list_data?.modifiers) {
        modifierIds.forEach((modifierId) => {
          const modifier = modifierList.modifier_list_data.modifiers.find((m) => m.id === modifierId)
          if (modifier?.modifier_data?.name) {
            modifierNames.push(modifier.modifier_data.name)
          }
        })
      }
    })

    const itemName = `${name}${variationName ? ` (${variationName})` : ""}${modifierNames.length > 0 ? ` + ${modifierNames.join(", ")}` : ""}`

    const newItem = {
      id: variationId, // This is your variation ID from Square
      itemId: id,
      name: itemName,
      price: totalPrice,
      image,
      quantity, // Include quantity as a property
      selectedModifiers,
      specialNote: specialNote.trim() || null,
    }

    // 🔍 DEBUG: Log what's being added to cart
    console.log("🛒 Adding to cart:", newItem)

    // Add the item to cart once with quantity property
    addToCart(newItem)

    // Set and show notification
    setNotificationMessage(`${name} added to cart!`)
    setShowNotification(true)
    setTimeout(() => {
      setShowNotification(false)
      setNotificationMessage("")
    }, 3000) // Notification disappears after 3 seconds
  }

  return (
    <div className="bg-[#F5F5DC] min-h-screen">
      <Navbar totalQuantity={totalQuantity} />

      <section className="w-full px-4 sm:px-6 md:px-8 lg:px-16 py-12 md:py-20 bg-orange-400">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-12 md:gap-20">
          {/* Image */}
          <div className="w-full lg:w-1/2">
            <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
              <img
                src={image || "https://via.placeholder.com/800"}
                alt={name || "Product"}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Details */}
          <div className="w-full lg:w-1/2 flex flex-col gap-6 md:gap-8">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-black">{name || "Product Name"}</h1>

            <p className="text-2xl font-bold text-black">${totalPrice.toFixed(2)}</p>

            <p className="text-base md:text-lg text-black">{description || "Product description goes here."}</p>

            {/* Variations */}
            {variations.length > 0 && (
              <div className="flex flex-col gap-2">
                <h3 className="text-lg font-semibold text-black">Choose an option</h3>
                <div className="flex flex-wrap gap-2">
                  {variations.map((variation) => {
                    const variationData = variation.item_variation_data
                    const variationName = variationData?.name || "Option"
                    const variationPrice = variationData?.price_money?.amount || 0

                    return (
                      <button
                        key={variation.id}
                        onClick={() => setSelectedVariation(variation)}
                        className={`px-4 py-2 rounded-md transition-colors ${
                          selectedVariation?.id === variation.id
                            ? "bg-black text-white"
                            : "bg-transparent text-black border border-black hover:bg-black hover:text-white"
                        }`}
                      >
                        {variationName} (${(variationPrice / 100).toFixed(2)})
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Modifiers */}
            {modifierListInfo.length > 0 && (
              <div className="flex flex-col gap-4">
                <h3 className="text-lg font-semibold text-black">Customize Your Order</h3>

                {loadingModifiers ? (
                  <div className="space-y-4">
                    {/* Skeleton for Modifier List 1 */}
                    <div className="border border-gray-300 rounded-lg p-4 bg-white/10 animate-pulse">
                      <div className="h-6 w-3/4 bg-gray-700 rounded mb-3"></div>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 border rounded-lg bg-white/5">
                          <div className="h-5 w-1/2 bg-gray-700 rounded"></div>
                          <div className="h-5 w-1/4 bg-gray-700 rounded"></div>
                        </div>
                        <div className="flex items-center justify-between p-3 border rounded-lg bg-white/5">
                          <div className="h-5 w-2/3 bg-gray-700 rounded"></div>
                          <div className="h-5 w-1/5 bg-gray-700 rounded"></div>
                        </div>
                      </div>
                    </div>
                    {/* Skeleton for Modifier List 2 (if applicable) */}
                    {modifierListInfo.length > 1 && (
                      <div className="border border-gray-300 rounded-lg p-4 bg-white/10 animate-pulse">
                        <div className="h-6 w-2/3 bg-gray-700 rounded mb-3"></div>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between p-3 border rounded-lg bg-white/5">
                            <div className="h-5 w-1/3 bg-gray-700 rounded"></div>
                            <div className="h-5 w-1/4 bg-gray-700 rounded"></div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  modifierListInfo.map((info) => {
                    const modifierList = modifierDetails[info.modifier_list_id]
                    if (!modifierList?.modifier_list_data) return null

                    const listData = modifierList.modifier_list_data
                    const listName = listData.name || "Options"
                    const modifiers = listData.modifiers || []
                    const selectionType = listData.selection_type || "MULTIPLE"
                    const minSelected = info.min_selected_modifiers || 0
                    const maxSelected = info.max_selected_modifiers || -1

                    return (
                      <div key={info.modifier_list_id} className="border border-gray-300 rounded-lg p-4 bg-white/10">
                        <h4 className="font-medium text-black mb-3">
                          {listName}
                          {minSelected > 0 && <span className="text-red-600 ml-1">*</span>}
                          {selectionType === "SINGLE" && (
                            <span className="text-sm text-gray-600 ml-2">(Choose one)</span>
                          )}
                          {maxSelected > 0 && maxSelected !== 1 && (
                            <span className="text-sm text-gray-600 ml-2">(Max {maxSelected})</span>
                          )}
                        </h4>

                        <div className="space-y-3">
                          {modifiers.map((modifier) => {
                            const modifierData = modifier.modifier_data
                            const modifierName = modifierData?.name || "Option"
                            const modifierPrice = modifierData?.price_money?.amount || 0
                            const isSelected = selectedModifiers[info.modifier_list_id]?.includes(modifier.id)

                            return (
                              <div
                                key={modifier.id}
                                onClick={() => toggleModifier(info.modifier_list_id, modifier.id)}
                                className="flex items-center justify-between p-3 border rounded-lg cursor-pointer hover:bg-white/20 bg-white/5 transition-colors"
                              >
                                <div className="flex items-center gap-3">
                                  {/* Custom Radio/Checkbox Dot */}
                                  <div className="relative">
                                    <div
                                      className={`w-5 h-5 rounded-full border-2 transition-all duration-200 ${
                                        isSelected ? "bg-black border-black" : "bg-white border-black"
                                      }`}
                                    ></div>
                                  </div>
                                  <span className="text-black font-medium">{modifierName}</span>
                                </div>
                                {modifierPrice > 0 && (
                                  <span className="text-black font-semibold">+${(modifierPrice / 100).toFixed(2)}</span>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )
                  })
                )}
              </div>
            )}

            {/* Special Note Section */}
            <div className="flex flex-col gap-2">
              <label className="text-lg font-semibold text-black">Special Instructions (Optional)</label>
              <textarea
                value={specialNote}
                onChange={(e) => setSpecialNote(e.target.value)}
                placeholder="Any special requests or dietary restrictions? Let us know here..."
                className="w-full p-3 border border-gray-300 rounded-md bg-white/90 text-black placeholder-gray-500 resize-none focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                rows={3}
                maxLength={200}
              />
              <div className="text-sm text-gray-600 text-right">{specialNote.length}/200 characters</div>
            </div>

            {/* Quantity */}
            <div className="flex flex-col gap-2 mt-4">
              <label className="text-lg font-semibold text-black">Quantity</label>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  className="w-10 h-10 flex items-center justify-center border border-black rounded-md hover:bg-black hover:text-white transition-colors"
                >
                  -
                </button>
                <span className="w-16 text-center border-b border-black py-2 text-black font-medium">{quantity}</span>
                <button
                  onClick={() => setQuantity((q) => q + 1)}
                  className="w-10 h-10 flex items-center justify-center border border-black rounded-md hover:bg-black hover:text-white transition-colors"
                >
                  +
                </button>
              </div>
            </div>

            {/* Add to Cart */}
            <button
              onClick={handleAddToCart}
              className="w-full bg-black hover:bg-gray-800 text-white font-semibold py-3 px-6 rounded-md transition-colors mt-4"
            >
              Add to Cart - ${(totalPrice * quantity).toFixed(2)}
            </button>
          </div>
        </div>
      </section>

      {/* Notification */}
      {showNotification && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 animate-fade-in-up">
          <CheckCircle className="w-6 h-6" />
          <span className="text-lg font-medium">{notificationMessage}</span>
        </div>
      )}
      <Footer />
    </div>
  )
}

export default Product

